import { api } from './client';
import { i18nMap } from '@/lib/i18n-map';

// ── Staff ───────────────────────────────────────────────────────────────

/** Categoria de calificare a medicului, cum o ține fișa angajatului. */
export type QualificationCategory = 'II' | 'I' | 'superioara';

export const QUALIFICATION_CATEGORY_LABELS = i18nMap<QualificationCategory>('common', {
  II: 'aIiA',
  I: 'i',
  superioara: 'superioara',
});

export interface StaffMember {
  /** Where they are, for the chat: online / busy / break / away. */
  chatStatus: 'online' | 'busy' | 'break' | 'away';
  id: string;
  name: string;
  role: string;
  level: string;
  spec: string | null;
  /** Categoria de calificare; se tipărește pe 039-1/e. */
  qualificationCategory: QualificationCategory | null;
  color: string;
  initials: string | null;
  email: string | null;
  phone: string | null;
  showInCalendar: boolean;
  workStart: string;
  workEnd: string;
  lunchStart: string | null;
  lunchEnd: string | null;
  photo: string | null;
  active: boolean;
  sortOrder: number;
  branchId: string | null;
  hiredAt: string | null;
  leftAt: string | null;
  jobTitle: string | null;
  hasLogin: boolean;
  /** Only present for callers who may see personnel records. */
  idnp?: string | null;
  birthDate?: string | null;
  address?: string | null;
  baseSalary?: number | null;
  leaveDaysYear?: number | null;
}

/**
 * What the API accepts when creating a team member.
 *
 * Written out rather than derived from `StaffMember`: validation runs with
 * `forbidNonWhitelisted`, so sending back a read-only field the list returns —
 * `id`, `hasLogin`, `sortOrder` — fails the whole request rather than being
 * ignored. Deriving it from the read model is how that shipped once already.
 */
export interface CreateStaffInput {
  name: string;
  role: string;
  level?: string;
  spec?: string | null;
  qualificationCategory?: QualificationCategory | null;
  color?: string;
  initials?: string | null;
  email?: string | null;
  phone?: string | null;
  showInCalendar?: boolean;
  workStart?: string;
  workEnd?: string;
  lunchStart?: string | null;
  lunchEnd?: string | null;
  hiredAt?: string | null;
  leftAt?: string | null;
  jobTitle?: string | null;
  idnp?: string | null;
  birthDate?: string | null;
  address?: string | null;
  baseSalary?: number | null;
  leaveDaysYear?: number | null;
  branchId?: string | null;
}

/** As above, plus the fields that only make sense on an existing member. */
export interface UpdateStaffInput extends Partial<CreateStaffInput> {
  active?: boolean;
}

export interface CreateServiceInput {
  name: string;
  durationMin?: number;
  price?: number;
  priceLabel?: string | null;
  category?: string | null;
  emoji?: string | null;
  color?: string;
  sortOrder?: number;
  reportRubric?: string | null;
  /** Exact leaf column of form 039-1/e this service fills. */
  f039Key?: string | null;
  /** Unități convenționale de muncă per unit. */
  ucm?: number;
  treats?: string[];
  isHygiene?: boolean;
  /** După o vizită finalizată la el pleacă instrucțiunile post-procedură (NOT-1). */
  postopCare?: boolean;
  /** Acordul cerut înainte de procedură (etapa 9, G2); null = niciunul. */
  consentTemplateId?: string | null;
  /** Tratamentul TVA (contabilitate §2.7); implicit scutit, art. 103 alin. (1) pct. 10. */
  vatTreatment?: VatTreatment;
  /** Grupa pentru registru; goală — contează categoria. */
  serviceGroup?: string | null;
}

/**
 * `reduced` = dispozitivele din Registrul de stat (8% până la 30.04.2026, apoi
 * cota standard — Legea 41/2026), păstrat pentru istorie;
 * `reduced_medical_goods` = 8% din art. 96 lit. b) liniuța a patra.
 */
export const VAT_TREATMENTS = [
  'exempt_103_10',
  'standard',
  'reduced',
  'reduced_medical_goods',
  'out_of_scope',
] as const;
export type VatTreatment = (typeof VAT_TREATMENTS)[number];

export interface UpdateServiceInput extends Partial<CreateServiceInput> {
  active?: boolean;
}

/** Un bloc din ziua unui medic: „Cabinet 3", „doar consultații", sau o oră
 *  indisponibilă (fără nume). */
export interface ScheduleBlock {
  id?: string;
  name: string | null;
  allowOnly: string | null;
  from: string;
  to: string;
  /** 1 = luni … 7 = duminică. Gol = doar ziua aleasă. */
  weekdays: number[] | null;
}

export interface DaySchedule {
  staffId: string;
  date: string;
  workStart: string;
  workEnd: string;
  lunchStart: string | null;
  lunchEnd: string | null;
  noLunch: boolean;
  blocks: ScheduleBlock[];
  /** Doar pe ziua unui singur om: clinica nu lucrează atunci. */
  clinicClosed?: boolean;
  /** Doar pe ziua unui singur om: felul concediului aprobat, dacă e în concediu. */
  onLeave?: string | null;
}

/** Ce trimite dialogul „Program de lucru" — scris pe larg, fiindcă API-ul
 *  refuză orice proprietate în plus. */
export interface SaveDayScheduleInput {
  date: string;
  workStart?: string;
  workEnd?: string;
  lunchStart?: string | null;
  lunchEnd?: string | null;
  noLunch?: boolean;
  blocks?: Array<{
    name?: string | null;
    allowOnly?: string | null;
    from: string;
    to: string;
    weekdays?: number[];
  }>;
}

export const staffApi = {
  /**
   * A colleague's way in.
   *
   * The password comes back once and is never readable again — the server
   * keeps only its hash, the same as for any other account. Whoever creates it
   * has to hand it over then, or reset it.
   */
  createLogin: (id: string) => api.post<{ email: string; password: string }>(`/staff/${id}/login`),

  /**
   * Invitația: un link cu termen, din care colegul își pune singur parola.
   *
   * Linkul se întoarce oricum, fie că a plecat și pe email, fie că nu —
   * `emailed` spune care din două, iar linkul se poate copia și da din mână.
   */
  invite: (id: string, sendEmail: boolean) =>
    api.post<{ email: string; link: string; expiresAt: string; emailed: boolean }>(
      `/staff/${id}/invite`,
      { sendEmail },
    ),
  resetLogin: (id: string) =>
    api.post<{ email: string; password: string }>(`/staff/${id}/login/reset`),
  revokeLogin: (id: string) => api.delete<void>(`/staff/${id}/login`),

  list: (params: { includeInactive?: boolean; calendarOnly?: boolean; role?: string } = {}) =>
    api.get<StaffMember[]>('/staff', { query: params }),
  create: (body: CreateStaffInput) => api.post<StaffMember>('/staff', body),
  /** Programul unei zile pentru un medic. */
  daySchedule: (id: string, date: string) =>
    api.get<DaySchedule>(`/staff/${id}/schedule`, { query: { date } }),

  /** Programul zilei pentru toți medicii din calendar. */
  dayScheduleAll: (date: string) =>
    api.get<DaySchedule[]>('/staff/schedule/day', { query: { date } }),

  saveDaySchedule: (id: string, body: SaveDayScheduleInput) =>
    api.put<DaySchedule>(`/staff/${id}/schedule`, body),

  update: (id: string, body: UpdateStaffInput) => api.patch<StaffMember>(`/staff/${id}`, body),
  reorder: (items: Array<{ id: string; sortOrder: number }>) =>
    api.put<void>('/staff/order', { items }),
  deactivate: (id: string) => api.delete<void>(`/staff/${id}`),
};

// ── Services ────────────────────────────────────────────────────────────

export interface ClinicService {
  id: string;
  name: string;
  durationMin: number;
  price: number;
  priceLabel: string | null;
  category: string | null;
  emoji: string | null;
  color: string | null;
  /** Acesta e serviciul de igienizare — de la el se numără reminderul. */
  isHygiene: boolean;
  /** După o vizită finalizată la el pleacă instrucțiunile post-procedură (NOT-1). */
  postopCare: boolean;
  /** Acordul cerut înainte de procedură (etapa 9, G2). */
  consentTemplateId: string | null;
  active: boolean;
  sortOrder: number;
  reportRubric: string | null;
  /** Exact leaf column of form 039-1/e this service fills. */
  f039Key: string | null;
  /** Unități convenționale de muncă per unit. */
  ucm: number;
  /** Dental-chart status keys this service treats. */
  treats: string[];
  /** `S-00001`, stabil pentru totdeauna — numele se schimbă, codul nu (contabilitate §2.7). */
  code: string;
  vatTreatment: VatTreatment;
  /** Scutit, dar pare cosmetic (CF art. 103 alin. (1) pct. 10)); lipsește cu contabilitatea oprită. */
  vatWarning?: string | null;
  serviceGroup: string | null;
  /** Specialitatea din care face parte; null = neclasat, deci vizibil mereu. */
  specialty: string | null;
  /** Prețul e cel propus de pachetul de pornire, încă neconfirmat de clinică. */
  priceSuggested: boolean;
}

/** A service-mappable column of form 039-1/e (option in the mapping dropdown). */
export interface F039Leaf {
  key: string;
  label: string;
  variant: 'terapeut' | 'ortodont' | 'chirurg' | 'protetician' | 'comune';
}

export const servicesApi = {
  list: (params: { includeInactive?: boolean } = {}) =>
    api.get<ClinicService[]>('/services', { query: params }),
  f039Columns: () => api.get<F039Leaf[]>('/services/f039-columns'),
  create: (body: CreateServiceInput) => api.post<ClinicService>('/services', body),
  update: (id: string, body: UpdateServiceInput) =>
    api.patch<ClinicService>(`/services/${id}`, body),
  deactivate: (id: string) => api.delete<void>(`/services/${id}`),
  /** Moves every service of `from` into `to` (or ungroups it, with `to: null`) in one call. */
  moveGroup: (from: string, to: string | null) =>
    api.post<{ moved: number }>('/services/move-group', { from, to }),
  /** How many untouched, unused starter services the clinic still carries. */
  starter: () => api.get<{ retirable: number }>('/services/starter'),
  /** Withdraws them all at once; returns which ones. */
  retireStarter: () =>
    api.post<{ retired: Array<{ id: string; name: string }> }>('/services/starter/retire'),
  /** Ce aduce pachetul fiecărei specialități, și ce are deja clinica. */
  packs: () => api.get<ServicePack[]>('/services/packs'),
  /** Adaugă din pachet numai ce lipsește; nu atinge niciun serviciu existent. */
  installPack: (key: string) =>
    api.post<{ added: string[] }>(`/services/packs/${encodeURIComponent(key)}/install`),
  /** Ia semnul „preț orientativ" de pe tot tarifarul, fără să schimbe cifrele. */
  confirmPrices: () => api.post<{ confirmed: number }>('/services/prices/confirm'),
};

/** Pachetul de pornire al unei specialități, față în față cu tarifarul clinicii. */
export interface ServicePack {
  key: string;
  label: string;
  description: string;
  /** Serviciile pachetului pe care clinica nu le are. */
  missing: string[];
  /** Câte are deja. */
  have: number;
  /** Câte au încă prețul propus de noi. */
  suggested: number;
}
